import React, { useState, useEffect, useMemo } from "react";
import { Box, TextField, Button, Typography, Grid2, Stack, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TablePagination, Checkbox, InputAdornment } from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify";
import Header from "../Header";
import Footer from "../Footer";
import { API_URL } from "../Config";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from 'sonner';
import jsPDF from "jspdf";
import "jspdf-autotable";
import config from "../common/Config";
import "../common/commonStyles.css";
import { fetchCompanyDetailsV2 } from "../../utils/companyDetailsApi";

const InvoicePaymentAllocation = ({
  customer_id: propCustomerId,
  fromDate: propFromDate,
  toDate: propToDate,
  onClose,
  onSuccess,
  API_URL: propApiUrl,
  accessToken: propAccessToken,
  customerName: propCustomerName,
  isModal = false,
}) => {
  const navigate = useNavigate();
  const { customer_id: paramCustomerId } = useParams();
  const customer_id = propCustomerId || paramCustomerId;
  const accessToken = propAccessToken || localStorage.getItem("accessToken");
  const apiUrl = propApiUrl || API_URL;

  const [fromDate, setFromDate] = useState(propFromDate || "");
  const [toDate, setToDate] = useState(propToDate || "");
  const [allocationAmount, setAllocationAmount] = useState(0);
  const [order, setOrder] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceAllocationAmount, setInvoiceAllocationAmount] = useState(0);
  const [checkedInvoices, setCheckedInvoices] = useState(new Set());
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState("");
  const token = localStorage.getItem("token");

  const [companyDetails, setCompanyDetails] = useState(null);
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const company = await fetchCompanyDetailsV2();
        if (company) {
          setCompanyDetails(company);
        }
      } catch {
        setCompanyDetails(null);
      }
    };
    fetchCompanyDetails();
  }, [apiUrl, accessToken]);

  const printReceiptDirectly = async (receiptInfo) => {
    const doc = new jsPDF();
    const margin = 14;
    let y = 15;

    // Define logo variables
    // const logoWidth = 40;
    // const logoHeight = 18;
    // // Use logo from API if available, else fallback
    // const logoUrl = companyDetails?.logo || `${config.BASE_URL}/assets/img/${accessToken}_logo.png`;

    // Use company details from API if available, else fallback to static
    const company = companyDetails || {};
    const companyName = company.company_name || "";
    // Address as a single line or split by '|' for multi-line
    const addressLines = company.address
      ? company.address.split("|").map((line) => line.trim())
      : [""];
    const phone = company.phone || "";
    const email = company.email || "";
    const companyNo = company.company_no || "";
    const vatNo = company.vat_no || "";
    const website = company.website || "";

    const logoUrl = `${config.BASE_URL}/assets/img/${accessToken}_logo.png`;
    const logoWidth = 40;
    const logoHeight = 18;

    let logoImg = null;
    try {
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      logoImg = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      logoImg = null;
    }

    if (logoImg) {
      try {
        doc.addImage(logoImg, "PNG", margin, y, logoWidth, logoHeight);
      } catch (e) { }
    }

    const detailsX = margin + logoWidth + 5;
    let detailsY = y + 2;

    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    doc.text(companyName, detailsX, detailsY);
    detailsY += 6;

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    addressLines.forEach((line) => {
      doc.text(line, detailsX, detailsY);
      detailsY += 4;
    });

    detailsY += 2;
    doc.text(
      `Phone No : ${phone}${email ? ` | Email : ${email}` : ""}`,
      detailsX,
      detailsY
    );
    detailsY += 4;
    const formatDate = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };
    // Only print companyNo/vatNo/website if present
    let extraLine = [];
    if (companyNo) extraLine.push(`Company No : ${companyNo}`);
    if (vatNo) extraLine.push(`VAT No : ${vatNo}`);
    if (website) extraLine.push(`Website : ${website}`);
    if (extraLine.length > 0) {
      doc.text(extraLine.join(" | "), detailsX, detailsY);
    }

    y += logoImg ? logoHeight + 8 : detailsY - y + 4;
    const summaryRows = [
      [
        {
          content: `Customer: ${receiptInfo?.customer_name || "N/A"}`,
          colSpan: 2,
          styles: { fontStyle: "bold" },
        },
        {
          content: "Amt Available",
          styles: { fontStyle: "bold", textAlign: "right" },
        },
        Number(allocationAmount).toFixed(2),
      ],
      [
        { content: "Allocated Amount", styles: { fontStyle: "bold" } },
        Number(
          receiptInfo?.allocated_amount || invoiceAllocationAmount
        ).toFixed(2),
        { content: "Date", styles: { fontStyle: "bold", textAlign: "right" } },
        formatDate(new Date()),
      ],
    ];

    doc.autoTable({
      body: summaryRows,
      startY: y,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 2,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 35, halign: "right" },
        2: { cellWidth: 40 },
        3: { cellWidth: 35, halign: "right" },
      },
      didParseCell: function (data) {
        if (
          data.cell.raw &&
          data.cell.raw.styles &&
          data.cell.raw.styles.fontStyle === "bold"
        ) {
          data.cell.styles.fontStyle = "bold";
        }
        if (data.column.index === 1 || data.column.index === 3) {
          data.cell.styles.halign = "right";
        }
      },
    });

    // Calculate correct allocated amount per invoice
    const tableColumn = [
      "Sr. No.",
      "Invoice Number",
      "Status",
      "Grand Total",
      "Previous Paid Amt",
      "Allocated",
    ];
    const tableRows = [];

    (receiptInfo?.invoices || selectedInvoices).forEach((inv, index) => {
      const grandTotal = Number(
        inv.grandTotal || inv.balance || inv.allocated || 0
      );
      const previousPaidAmt = Number(inv.previous_paid || inv.paid || 0);
      const allocatedAmt = Number(inv.paid || inv.allocated || 0);

      tableRows.push([
        index + 1, // ✅ Sr. No.
        String(inv.reference_no || inv.invoiceNumber || inv.id || ""),
        String(inv.payment_status || "Allocated"),
        grandTotal.toFixed(2),
        previousPaidAmt.toFixed(2),
        allocatedAmt.toFixed(2),
      ]);
    });

    const safeTableRows = tableRows.filter(
      (row) => Array.isArray(row) && row.length === tableColumn.length
    );

    // ✅ Totals
    const totalGrand = safeTableRows.reduce(
      (sum, r) => sum + parseFloat(r[3] || 0),
      0
    );
    const totalPrevPaid = safeTableRows.reduce(
      (sum, r) => sum + parseFloat(r[4] || 0),
      0
    );
    const totalAllocated = safeTableRows.reduce(
      (sum, r) => sum + parseFloat(r[5] || 0),
      0
    );

    // ✅ Footer row (shifted by one column because of Sr. No.)
    safeTableRows.push([
      {
        content: "Total",
        colSpan: 3,
        styles: { halign: "right", fontStyle: "bold" },
      },
      totalGrand.toFixed(2),
      totalPrevPaid.toFixed(2),
      totalAllocated.toFixed(2),
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: safeTableRows,
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 5 : y,
      styles: {
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 30, halign: "center" }, // Sr. No.
        1: { cellWidth: 40 }, // Invoice Number
        2: { cellWidth: 25 }, // Status
        3: { cellWidth: 30, halign: "right" }, // Grand Total
        4: { cellWidth: 35, halign: "right" }, // Previous Paid Amt
        5: { cellWidth: 35, halign: "right" }, // Allocated
      },
    });

    doc.autoPrint(); // trigger print mode
    window.open(doc.output("bloburl"), "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedInvoices.length === 0) {
      toast.warning("Please select at least one invoice");
      return;
    }

    // Validate selection and show specific errors
    const validationResult = validateSelection();
    if (!validationResult.isValid) {
      toast.warning(validationResult.message);
      return;
    }

    const formData = new FormData();
    formData.append("customer_id", customer_id);
    selectedInvoices.forEach((invoice) => {
      formData.append("ids[]", invoice.invoiceNumber);
      formData.append("reference_no[]", invoice.reference_no);
      formData.append("sources[]", invoice.source);
    });

    try {
      const response = await fetch(`${apiUrl}/SubmitInvoicePaymentAllocation`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        // Map API response to receipt format for printing
        const receiptInvoices = (data.invoices || []).map((inv) => ({
          reference_no: inv.reference_number,
          grandTotal: inv.grand_total,
          previous_paid: inv.previous_paid,
          paid: inv.paid,
          payment_status: inv.payment_status,
          allocated: inv.paid, // Use paid as allocated for this API
        }));

        // Try to get customer name from API response, fallback to prop or selectedInvoices
        const customerNameFromApi =
          data.customer_name ||
          data.customer ||
          (data.data && (data.data.customer_name || data.data.customer)) ||
          propCustomerName ||
          selectedInvoices[0]?.customer_name ||
          selectedInvoices[0]?.customer ||
          "N/A";

        const receiptInfo = {
          customer_name: customerNameFromApi,
          date: new Date().toLocaleDateString(),
          allocated_amount: receiptInvoices.reduce(
            (sum, inv) => sum + parseFloat(inv.paid || 0),
            0
          ),
          invoices: receiptInvoices,
        };
        setReceiptData(receiptInfo);

        setSuccessDialogMessage(
          data.message ||
            "Invoice payment allocation submitted successfully!"
        );
        setSuccessDialogOpen(true);
        setSelectedInvoices([]);
        setInvoiceAllocationAmount(0);
        setCheckedInvoices(new Set());
        fetchData();
      } else {
        toast.error(data.message || "Failed to allocate payment");
      }
    } catch (error) {
      console.error("Error submitting allocation:", error);
      toast.error("An error occurred while submitting allocation");
    }
  };

  const fetchData = async () => {
    if (!customer_id) return;

    const formData = new FormData();
    formData.append("searchByFromdate", fromDate);
    formData.append("searchByTodate", toDate);
    formData.append("customer_id", customer_id);

    try {
      const response = await fetch(`${apiUrl}/InvoicePaymentAllocation`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      const data = await response.json();
      if (data.success && data.data) {
        setOrder(data.data.customer_order || []);
        setAllocationAmount(data.data.allocation_amout[0]?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // FIX 1: Separate calculation function for allocation amount
  const calculateAllocationAmount = (invoices) => {
    return invoices.reduce((total, invoice) => {
      return total + parseFloat(invoice.grandTotal || 0);
    }, 0);
  };

  // Helper function to validate selection and return specific error messages
  const validateSelection = () => {
    if (selectedInvoices.length === 0) {
      return { isValid: false, message: "Please select at least one invoice" };
    }

    const availableAmount = parseFloat(allocationAmount);
    const totalSelectedAmount = selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.grandTotal || 0), 0);

    if (totalSelectedAmount <= availableAmount) {
      return { isValid: true, message: "" }; // All invoices can be fully paid
    }

    // Check partial payment rules
    const partiallyPayableInvoices = selectedInvoices.filter(inv => parseFloat(inv.grandTotal || 0) > availableAmount);
    const fullyPayableInvoices = selectedInvoices.filter(inv => parseFloat(inv.grandTotal || 0) <= availableAmount);

    if (partiallyPayableInvoices.length > 1) {
      // Multiple partially payable invoices - not allowed
      const invoiceNumbers = partiallyPayableInvoices.map(inv => inv.reference_no || inv.invoiceNumber).join(', ');
      return {
        isValid: false,
        message: `Only one invoice can be partially paid at a time.<br><strong>${invoiceNumbers}</strong><br><br>You can keep only one of them selected.`
      };
    }

    if (partiallyPayableInvoices.length === 1) {
      const totalFullyPayable = fullyPayableInvoices.reduce((sum, inv) => sum + parseFloat(inv.grandTotal || 0), 0);
      if (totalFullyPayable > availableAmount) {
        // Too many fully payable invoices
        const invoiceNumbers = fullyPayableInvoices.map(inv => inv.reference_no || inv.invoiceNumber).join(', ');
        return {
          isValid: false,
          message: `Insufficient funds to pay all selected fully payable invoices.<br><br>Please uncheck some of these invoices:<br><strong>${invoiceNumbers}</strong><br><br>Available amount: ${availableAmount.toFixed(2)}<br>Required amount: ${totalFullyPayable.toFixed(2)}`
        };
      }
    }

    return { isValid: true, message: "" };
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, customer_id]);

  const filteredOrder = useMemo(() => {
    if (!searchTerm) return order;
    const term = searchTerm.toLowerCase();
    return order.filter((item) => {
      return (
        (item.reference_no && item.reference_no.toLowerCase().includes(term)) ||
        (item.invoice_date && item.invoice_date.toLowerCase().includes(term)) ||
        (item.grand_total && String(item.grand_total).includes(term)) ||
        (item.paid && String(item.paid).includes(term)) ||
        (item.balance && String(item.balance).includes(term))
      );
    });
  }, [order, searchTerm]);

  const paginatedOrder = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredOrder.slice(start, end);
  }, [filteredOrder, page, rowsPerPage]);

  const handleCheckboxChange = (invoice, isChecked) => {
    let newSelectedInvoices;
    let newCheckedInvoices;

    if (isChecked) {
      const invoiceData = {
        invoiceNumber: invoice.id,
        reference_no: invoice.reference_no,
        grandTotal: parseFloat(invoice.balance || 0),
        source: invoice.source,
      };
      newSelectedInvoices = [...selectedInvoices, invoiceData];
      newCheckedInvoices = new Set([...checkedInvoices, invoice.id]);
    } else {
      newSelectedInvoices = selectedInvoices.filter(
        (inv) => inv.invoiceNumber !== invoice.id
      );
      newCheckedInvoices = new Set(checkedInvoices);
      newCheckedInvoices.delete(invoice.id);
    }

    const newAllocated = newSelectedInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.grandTotal || 0),
      0
    );

    setSelectedInvoices(newSelectedInvoices);
    setInvoiceAllocationAmount(newAllocated.toFixed(2));
    setCheckedInvoices(newCheckedInvoices);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // FIX 5: Remove the duplicate useEffect that was overriding the calculation
  // This useEffect was causing the issue by recalculating after the checkbox event
  // useEffect(() => {
  //   const newTotal = selectedInvoices.reduce((total, inv) => total + inv.grandTotal, 0);
  //   setInvoiceAllocationAmount(newTotal.toFixed(2));
  // }, [selectedInvoices]);

  const renderReceiptModal = () => {
    return (
      <Dialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '100vh',
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          px: 3,
          py: 1.5,
          mb: 2,
          borderBottom: '1px dashed #E0E0E0'
        }}>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            Payment Allocation Receipt
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadPDF}
              disabled={order.length === 0}
              startIcon={<Iconify icon="mdi:file-pdf-box" width={20} height={20} />}
              sx={{
                borderColor: '#9E9E9E',
                color: '#424242',
                '&:hover': {
                  borderColor: '#757575'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                  borderColor: '#e0e0e0'
                }
              }}
            >
              Download PDF
            </Button>
            <IconButton
              onClick={() => setShowReceipt(false)}
              sx={{ color: 'text.secondary' }}
              size="small"
            >
              <Iconify icon="mdi:close" width={24} height={24} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box id="allocation-receipt-print">
            <Typography variant="h5" fontWeight={600} textAlign="center" sx={{ mb: 2.5 }}>
              Payment Allocation Receipt
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2.5 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: "30%", fontWeight: 600 }}>
                      Customer
                    </TableCell>
                    <TableCell>
                      {receiptData?.customer_name ||
                        receiptData?.customer ||
                        "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Date
                    </TableCell>
                    <TableCell>
                      {receiptData?.date || new Date().toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Allocated Amount
                    </TableCell>
                    <TableCell>
                      {Number(
                        receiptData?.allocated_amount ||
                        invoiceAllocationAmount
                      ).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" fontWeight={600} sx={{ mt: 2.5, mb: 1.25 }}>
              Allocated Invoices
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Allocated Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(receiptData?.invoices || selectedInvoices).map(
                    (inv, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          {inv.invoiceNumber || inv.reference_no || inv.id}
                        </TableCell>
                        <TableCell align="right">
                          {Number(inv.allocated || inv.grandTotal).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2.5, textAlign: "right" }}>
              <Typography variant="body1" fontWeight={600}>
                Total Allocated:{" "}
                {Number(
                  receiptData?.allocated_amount || invoiceAllocationAmount
                ).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px dashed #E0E0E0' }}>
          <Button
            variant="outlined"
            onClick={() => setShowReceipt(false)}
            sx={{
              borderColor: '#9E9E9E',
              color: '#424242',
              '&:hover': {
                borderColor: '#757575'
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handlePrintReceipt}
            sx={{
              backgroundColor: '#F8991D',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#F8991D'
              }
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handlePrintReceipt = () => {
    if (receiptData) {
      printReceiptDirectly(receiptData);
    }
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);
    fetchData();
  };

  // Download function
  const handleDownloadPDF = () => {
    if (order.length === 0) return;

    const doc = new jsPDF();
    const margin = 14;
    let y = 15;

    // Add company details if available
    const company = companyDetails || {};
    const companyName = company.company_name || "Company Name";

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Unpaid Invoice Payment", margin, y);
    y += 10;

    doc.setFontSize(12);
    doc.setFont(undefined, "normal");
    doc.text(`Company: ${companyName}`, margin, y);
    y += 6;

    // Add customer name
    const customerName = propCustomerName || selectedInvoices[0]?.customer_name || selectedInvoices[0]?.customer || "N/A";
    doc.text(`Customer: ${customerName}`, margin, y);
    y += 6;

    // Format date as d-m-Y
    const formatDate = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    doc.text(`Date: ${formatDate(new Date())}`, margin, y);
    y += 6;
    doc.text(`Available Amount: ${Number(allocationAmount).toFixed(2)}`, margin, y);
    y += 10;

    // Table headers
    const tableHeaders = [
      "S.No",
      "Date",
      "Invoice No",
      "Amount",
      "Paid",
      "Balance"
    ];

    // Table data
    const tableData = order.map((invoice, index) => [
      index + 1,
      invoice.invoice_date ? invoice.invoice_date : "",
      invoice.reference_no || "",
      parseFloat(invoice.grand_total || 0).toFixed(2),
      parseFloat(invoice.paid || 0).toFixed(2),
      parseFloat(invoice.balance || 0).toFixed(2)
    ]);

    // Add totals
    const totalAmount = order.reduce((sum, inv) => sum + parseFloat(inv.grand_total || 0), 0);
    const totalPaid = order.reduce((sum, inv) => sum + parseFloat(inv.paid || 0), 0);
    const totalBalance = order.reduce((sum, inv) => sum + parseFloat(inv.balance || 0), 0);

    tableData.push([
      "",
      "",
      "TOTAL",
      totalAmount.toFixed(2),
      totalPaid.toFixed(2),
      totalBalance.toFixed(2)
    ]);

    doc.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: y,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: 15, halign: "center" }, // S.No
        1: { cellWidth: 30 }, // Date
        2: { cellWidth: 40 }, // Invoice No
        3: { cellWidth: 30, halign: "right" }, // Amount
        4: { cellWidth: 30, halign: "right" }, // Paid
        5: { cellWidth: 30, halign: "right" }, // Balance
      },
      didParseCell: function (data) {
        // Make totals row bold
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
        // Right align numeric columns
        if ([3, 4, 5].includes(data.column.index)) {
          data.cell.styles.halign = "right";
        }
      },
    });

    doc.save(`invoice_payment_allocation_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const renderMainContent = () => {
    return (
      <>
        <Grid2 container spacing={2} mt={5} mb={1}>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              className="input-border"
              fullWidth
              label="Available Amount"
              value={Number(allocationAmount).toFixed(2)}
              inputProps={{
                readOnly: true,
                style: { textAlign: 'right' }
              }}
              size="small"
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              className="input-border"
              fullWidth
              label="Allocated Amount"
              value={invoiceAllocationAmount}
              inputProps={{
                readOnly: true,
                style: { textAlign: 'right' }
              }}
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: parseFloat(invoiceAllocationAmount) > 0 ? "#e8f5e8" : "#fff",
                  fontWeight: parseFloat(invoiceAllocationAmount) > 0 ? 600 : 400,
                }
              }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 4}}>
          <TextField
              className="input-border"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              size="small"
      
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" width={20} height={20} sx={{ color: '#637381' }} />
                  </InputAdornment>
                ),
              }}
            />
            </Grid2>
        </Grid2>

        <Box mb={2}>  
            {/* <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.875rem' }}>
              {filteredOrder.length > 0
                ? `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, filteredOrder.length)} of ${filteredOrder.length} invoices`
                : 'No invoices found'}
            </Typography> */}
          <TableContainer sx={{ maxHeight: "60vh", overflow: "auto" }}>
            <Table className="table-cell-border">
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="80px">S.No</TableCell>
                  <TableCell width="100px">Date</TableCell>
                  <TableCell width="100px">Invoice Number</TableCell>
                  <TableCell align="right" width="100px">Amount</TableCell>
                  <TableCell align="right" width="100px">Paid</TableCell>
                  <TableCell align="right" width="100px">Balance</TableCell>
                  <TableCell width="100px">Allocate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrder.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No invoices found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedOrder.map((invoice, index) => {
                    const serialNo = page * rowsPerPage + index + 1;
                    const isChecked = checkedInvoices.has(invoice.id);
                    return (
                      <TableRow key={invoice.id || index} hover
                        className="table-cell-style"
                      >
                        <TableCell>{serialNo}</TableCell>
                        <TableCell>{invoice.invoice_date || '-'}</TableCell>
                        <TableCell>{invoice.reference_no || '-'}</TableCell>
                        <TableCell align="right">
                          {parseFloat(invoice.grand_total || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(invoice.paid || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(invoice.balance || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => handleCheckboxChange(invoice, e.target.checked)}
                            className="checkBox"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredOrder.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </TableContainer>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Button
              variant="contained"
              onClick={handleDownloadPDF}
              disabled={order.length === 0}
              className="button-variant-contained"
            >
              Download PDF
            </Button>
          </Box>
          <Stack direction="row" spacing={2}>
            {isModal && (
              <Button
                variant="outlined"
                className="button-variant-outlined"
                onClick={onClose}
              >
                Close
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={selectedInvoices.length === 0}
              sx={{
                backgroundColor: '#F8991D',
                color: '#fff',
                '&:hover': {
                  backgroundColor: '#F8991D'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Allocation Payment
            </Button>
          </Stack>
        </Stack>
      </>
    );
  };

  if (isModal) {
    return renderMainContent();
  }

  return (
    <Box className="main-container">
      <Box sx={{ mt: -9 }}>
        {showReceipt ? (
          <Paper
            elevation={1}
            sx={{
              maxWidth: 600,
              mx: "auto",
              my: 4,
              border: "1px solid #E0E0E0",
              p: 3,
              bgcolor: "#fff",
            }}
          >
            <Typography variant="h5" fontWeight={600} textAlign="center" sx={{ mb: 2.5 }}>
              Payment Allocation Receipt
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2.5 }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ width: "30%", fontWeight: 600 }}>
                      Customer
                    </TableCell>
                    <TableCell>
                      {receiptData?.customer_name ||
                        receiptData?.customer ||
                        "N/A"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Date
                    </TableCell>
                    <TableCell>
                      {receiptData?.date || new Date().toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      Allocated Amount
                    </TableCell>
                    <TableCell>
                      {Number(
                        receiptData?.allocated_amount || invoiceAllocationAmount
                      ).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 2.5, mb: 1.25 }}>
              Allocated Invoices
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Allocated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(receiptData?.invoices || selectedInvoices).map((inv, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{inv.reference_no}</TableCell>
                      <TableCell>{Number(inv.grandTotal).toFixed(2)}</TableCell>
                      <TableCell>{Number(inv.allocated).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCloseReceipt}
                sx={{
                  borderColor: '#9E9E9E',
                  color: '#424242',
                  '&:hover': {
                    borderColor: '#757575'
                  }
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handlePrintReceipt}
                sx={{
                  backgroundColor: '#F8991D',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#F8991D'
                  }
                }}
              >
                Print Receipt
              </Button>
            </Stack>
          </Paper>
        ) : (
          renderMainContent()
        )}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>{successDialogMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              if (receiptData) printReceiptDirectly(receiptData);
              setSuccessDialogOpen(false);
              if (onSuccess) onSuccess();
              if (!isModal) navigate("/account/customer_accounts");
            }}
            variant="contained"
            className="button-variant-contained"
          >
            Yes, Print Receipt
          </Button>
          <Button
            onClick={() => {
              setSuccessDialogOpen(false);
              if (onSuccess) onSuccess();
              if (!isModal) navigate("/account/customer_accounts");
            }}
            variant="outlined"
          >
            No, Continue
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default InvoicePaymentAllocation;

